// src/context/index.js
import React, { createContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  setAuth,
  clearAuth,
  bootstrapAuthFromStorage,
  scheduleTokenExpiry,
  verifyWithServer,       // (boolean in your current AuthService)
  refreshUserFromAPI,
  getAccessToken,
  getRefreshToken,
} from '../services/AuthService';

export const UserContext = createContext();

export function UserProvider({ children }) {
  const BASE = process.env.REACT_APP_BASE_URL;

  // Bootstrap token (sets axios authorization header if valid)
  const boot = bootstrapAuthFromStorage();

  // Safe read from cache
  const cachedUser = (() => {
    try { return JSON.parse(localStorage.getItem('satnam.user') || 'null'); }
    catch { return null; }
  })();

  const [state, setState] = useState({
    user: cachedUser,                      // may be null at first load
    auth: boot.ok ? getAccessToken() : null,
    ready: false,                          // becomes true after first hydration pass
    hydrating: boot.ok,                    // true while verifying + refreshing
  });

  const expiryTimerId = useRef(null);

  const writeCache = (user) => {
    if (user) localStorage.setItem('satnam.user', JSON.stringify(user));
    else localStorage.removeItem('satnam.user');
  };

  const stopExpiryTimer = () => {
    if (expiryTimerId.current) {
      clearTimeout(expiryTimerId.current);
      expiryTimerId.current = null;
    }
  };

  const clearAllAuth = () => {
    stopExpiryTimer();
    clearAuth();                 // clears AUTH_KEY/USER_KEY + axios header
    writeCache(null);
    setState({ user: null, auth: null, ready: true, hydrating: false });
  };

  const startExpiryTimer = () => {
    if (!state.auth) return;
    stopExpiryTimer();
    expiryTimerId.current = scheduleTokenExpiry(async () => {
      try {
        const refresh = getRefreshToken();
        if (refresh) {
          await axios.post(`${BASE}/api/logout/`, { refresh }, { timeout: 4000 });
        }
      } catch { /* best-effort */ }
      clearAllAuth();
    });
  };

  // One-time hydration (cache -> verify -> server)
  useEffect(() => {
    (async () => {
      // No token? We’re ready immediately (guest mode)
      if (!getAccessToken()) {
        setState(s => ({ ...s, ready: true, hydrating: false }));
        return;
      }

      // Your current verifyWithServer returns boolean and clears auth internally.
      const {ok} = await verifyWithServer(BASE);
      if (!ok) {                     // token invalid or server refused
        clearAllAuth();
        return;
      }

      try {
        const user = await refreshUserFromAPI(BASE);
        writeCache(user);
        setState({ user, auth: getAccessToken(), ready: true, hydrating: false });
      } catch (e) {
        console.error('Hydrate user failed:', e?.response?.data || e?.message);
        // Keep cached user, don’t log out; just mark ready so UI can render.
        setState(s => ({ ...s, ready: true, hydrating: false }));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep cache updated whenever user object changes
  useEffect(() => { writeCache(state.user); }, [state.user]);

  // Cross-tab sync (login/logout in another tab)
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'satnam.user') {
        const next = e.newValue ? JSON.parse(e.newValue) : null;
        setState(s => ({ ...s, user: next }));
      } else if (e.key === 'satnam.auth') {
        // Reflect token changes from other tabs
        const hasAuth = !!e.newValue;
        if (!hasAuth) {
          // If auth was cleared elsewhere, clear here too
          clearAllAuth();
        } else {
          // Auth set elsewhere – ensure axios header is on in this tab too
          setState(s => ({ ...s, auth: getAccessToken() }));
        }
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // (Re)start expiry timer when auth changes
  useEffect(() => {
    if (state.auth) startExpiryTimer();
    else stopExpiryTimer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.auth]);

  // ---- actions (use anywhere) ----
  async function logIn(username, password) {
    try {
      const { data } = await axios.post(
        `${BASE}/api/log_in/`,
        { username, password },
        { timeout: 5000 }
      );

      // Persist tokens + wire axios header
      setAuth(data);

      // Canonical user from server
      const user = await refreshUserFromAPI(BASE);
      writeCache(user);

      setState({ user, auth: getAccessToken(), ready: true, hydrating: false });
      startExpiryTimer();

      return { ok: true, data };
    } catch (error) {
      return { ok: false, error };
    }
  }

  async function logOut({ revoke = true } = {}) {
    try {
      if (revoke) {
        const refresh = getRefreshToken();
        if (refresh) {
          await axios.post(`${BASE}/api/logout/`, { refresh }, { timeout: 4000 });
        }
      }
    } catch { /* ignore network errors on logout */ }
    clearAllAuth();
  }

  async function refreshUser() {
    if (!state.auth) return null;
    try {
      const user = await refreshUserFromAPI(BASE);
      writeCache(user);
      setState(s => ({ ...s, user }));
      return user;
    } catch (e) {
      console.error('refreshUser failed:', e?.response?.status, e?.response?.data || e?.message);
      // Don’t nuke session on transient failures; keep existing user.
      return null;
    }
  }

  function updateUserLocally(patch) {
    setState(s => {
      const next = typeof patch === 'function' ? patch(s.user) : { ...s.user, ...patch };
      writeCache(next);
      return { ...s, user: next };
    });
  }

  const actions = useMemo(() => ({
    logIn,
    logOut,
    refreshUser,
    updateUserLocally,
    setState, // backward-compat with legacy [state, setState] usage
  }), []);

  return (
    <UserContext.Provider value={[state, setState, actions]}>
      {children}
    </UserContext.Provider>
  );
}
